import { useContext, useState } from "react";
import { Search, Plus, Layers } from "lucide-react";
import { PromptLibraryProvider, usePromptLibrary } from "./modal/PromptLibraryContext";
import PromptCard from "./ui/PromptCard";
import PromptForm from "./ui/PromptForm";
import type { Prompt, PromptCreate } from "./api/PromptLibraryApi";
import { VerifyContext } from "app/VerifyContext";

function PromptLibraryContent() {
  const {
    categories, prompts, promptsLoading, categoriesLoading,
    filters, activeCategoryId,
    setActiveCategoryId, setFilters,
    createPrompt, updatePrompt, deletePrompt,
  } = usePromptLibrary();

  const { superAdmin } = useContext(VerifyContext);
  const canEdit = superAdmin;

  const [showForm, setShowForm] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);

  function openAdd() { setEditingPrompt(null); setShowForm(true); }
  function openEdit(p: Prompt) { setEditingPrompt(p); setShowForm(true); }

  return (
    <div className="flex gap-6 h-full min-h-0">
      {/* Left panel — categories */}
      <div className="w-56 shrink-0 flex flex-col gap-1">
        <p className="text-[11px] text-muted-foreground font-inter font-medium uppercase tracking-wider px-2 mb-1">Categories</p>

        <button
          onClick={() => setActiveCategoryId(null)}
          className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm font-inter transition-colors ${
            activeCategoryId === null
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-surface-muted"
          }`}
        >
          <span className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            All Prompts
          </span>
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${activeCategoryId === null ? "bg-primary-foreground/20" : "bg-muted"}`}>
            {categories.reduce((s, c) => s + (c.prompt_count ?? 0), 0)}
          </span>
        </button>

        {categoriesLoading && (
          <div className="px-3 py-2 text-xs text-muted-foreground font-inter">Loading…</div>
        )}

        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategoryId(cat.id === activeCategoryId ? null : cat.id)}
            className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm font-inter transition-colors ${
              activeCategoryId === cat.id
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-surface-muted"
            }`}
          >
            <span className="truncate text-left">{cat.name}</span>
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full shrink-0 ${activeCategoryId === cat.id ? "bg-primary-foreground/20" : "bg-muted"}`}>
              {cat.prompt_count ?? 0}
            </span>
          </button>
        ))}
      </div>

      {/* Right panel — prompts */}
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={filters.search ?? ""}
              onChange={(e) => setFilters({ ...filters, search: e.target.value || undefined })}
              className="w-full bg-surface border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-inter"
              placeholder="Search prompts…"
            />
          </div>

          {canEdit && (
            <button
              onClick={openAdd}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-xl font-inter transition-colors ml-auto"
            >
              <Plus className="w-4 h-4" />
              Add Prompt
            </button>
          )}
        </div>

        {/* Stats bar */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground font-inter">
          <span>{prompts.length} prompt{prompts.length !== 1 ? "s" : ""}</span>
        </div>

        {/* Prompt list */}
        <div className="flex-1 space-y-3 overflow-y-auto">
          {promptsLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!promptsLoading && prompts.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <p className="font-inter text-sm">No prompts found.</p>
              {canEdit && (
                <button onClick={openAdd} className="mt-3 text-primary hover:text-primary/80 text-sm font-inter underline">
                  Add the first prompt
                </button>
              )}
            </div>
          )}

          {!promptsLoading && prompts.map((p) => (
            <PromptCard
              key={p.id}
              prompt={p}
              categories={categories}
              canEdit={canEdit}
              onEdit={openEdit}
              onDelete={deletePrompt}
            />
          ))}
        </div>
      </div>

      {/* Prompt form modal */}
      {showForm && (
        <PromptForm
          categories={categories}
          initial={editingPrompt}
          defaultCategoryId={activeCategoryId}
          onSave={editingPrompt
            ? (data) => updatePrompt(editingPrompt.id, data)
            : (data) => createPrompt(data as PromptCreate) // create mode: the form always sends a full payload
          }
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}

export default function PromptLibraryPage() {
  return (
    <PromptLibraryProvider>
      <div className="flex flex-col gap-6 h-full">
        <div>
          <h1 className="text-xl font-bold text-foreground font-inter">Prompt Library</h1>
          <p className="text-sm text-muted-foreground font-inter mt-0.5">Standard, ready-to-use prompts for LinkedIn topics, comments, images and more — fill in the blanks and copy.</p>
        </div>
        <PromptLibraryContent />
      </div>
    </PromptLibraryProvider>
  );
}
